import { Fragment, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { CommonCivilStatusCrumbs } from 'civil-status/constants/breadcrumb';
import StepForm from 'civil-status/components/step/form';
import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ActWizard, AttachmentsData } from 'shell/models/act-wizard';
import {
  DataWizardAct,
  IdType,
  LastStepVM,
  ParentVM,
  DocumentVM,
  QuestionarioEvento,
  RecapVM,
  RequestsType,
  StepVM,
  SoggettoList,
  ResponseValidateEvent,
  ErrorsWarnings,
  ResponsePreview,
  StatoCivile,
  Evento,
  SignatureActVM,
  SignatureActUscVM,
  AllegatoAtti,
  ViewStepVM,
  SectionVM,
  TextActVM,
} from 'civil-status/view-models/step-form';
import { transformerActWizard } from 'civil-status/utils/transformer-act-wizard';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { transformerActWizardToSave } from 'civil-status/utils/transformer-act-wizard-to-save';
import { AxiosResponse } from 'axios';
import { SectionWizardUseCase, SubjectsWizardUseCase } from 'shell/models/subjects-wizard';
import {
  initialValuesParent,
  initialValuesSections,
} from 'civil-status/constants/initial-values-step';
import {
  InputCreate,
  createSchemaSubjectSectionStep,
} from 'civil-status/constants/schema-subject-section-step';
import { transformerActWizardDataSaved } from 'civil-status/utils/transformer-act-wizard-data-saved';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { DateTime } from 'luxon';
import { CodeSection, CodeSubject } from 'civil-status/constants/code-subject-section';
import { ID_GENDERS } from 'shell/components/shared/gender';
import { transformerActSignature } from 'civil-status/utils/transformer-act-signature/index';
import { transformerActSignatureUsc } from 'civil-status/utils/transformer-act-signature-usc';
import { schemaSignatureUsc } from 'civil-status/constants/schema-signature-usc';
import { noop } from 'shell/utils/noop';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { STATES_ACT } from 'civil-status/constants/states-act';
import { transformerActAnsc } from 'civil-status/utils/transformer-act-ansc';
import { ActCategory } from 'shell/models/act-category-list';
import { codResponseDelay } from 'civil-status/constants/cod-response-delay';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { schemaCertificateConformity } from 'civil-status/constants/schema-certificate-conformity';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { StateActProps } from 'civil-status/view-models/state-act';
import TitleStep from 'civil-status/components/shared/title-step';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { AnscRegisterEmergency } from 'shell/authorizations/civil-status';
import { ID_NUMBERS_COMPARENTI } from 'civil-status/components/step/form/last-step/accordions/section/list/list-number';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';
import { CodeFormatBirthDate, valueCompleta } from 'civil-status/constants/code-format-birth-date';

// const serviceCode = '99';

const messageModalGoBack =
  'Tornare indietro comporta la cancellazione dei metadati salvati. Confermi di voler ricominciare la compilazione?';
const messageModalPreview =
  "Se si vuole che le modifiche in sospeso si riflettano sull'anteprima occorre prima salvarle. Continuare senza salvare i dati in sospeso?";
const messageModalDelete = "Una volta eliminato, l'evento non è più disponibile. Vuoi procedere?";

export type ErrorWarningToVisualizeType = {
  visibilityError?: boolean;
  titleError?: string;
  messagesError?: Array<ItemVM>;
  visibilityWarning?: boolean;
  titleWarning?: string;
  messagesWarning?: Array<ItemVM>;
};

interface StepPageProps {
  isEdit?: boolean;
  isDetail?: boolean;
  isTranscribeOathFromAnsc?: boolean;
  isStepFromEmergencyConfirm?: boolean;
}

const StepPageComponent = ({
  isEdit = false,
  isDetail = false,
  isTranscribeOathFromAnsc = false,
  isStepFromEmergencyConfirm = false,
}: StepPageProps): React.ReactElement => {
  // stato riepilogo
  const [recap, setRecap] = useState<Array<RecapVM>>([]);
  // stato dell'atto
  const [stateAct, setStateAct] = useState<StateActProps>({
    isLastStep: false,
    isLastStepEditAct: false,
    isSignatureAct: false,
    isCertificateConformity: false,
    isSignatureUSCWithoutDocument: false,
    isCertificateConformityWithoutSubjects: false,
    isSignatureUSC: false,
    isCompleteSignature: false,
    isTranscribeOath: false,
    isLastStepEmergency: false,
    allegatoList: [],
    idAnsc: undefined,
    idEvento: undefined,
  });
  const [lastStepInitialData, setLastStepInitialData] = useState<LastStepVM>({});
  const [deleteAlertModal, setDeleteAlertModal] = useState<{
    open: boolean;
    step?: string;
    index?: number;
    message?: string;
    errors?: Array<ErrorsWarnings>;
    forcingCode?: string;
  }>({ open: false });
  const [modalErrorSave, setModalErrorSave] = useState<{
    open: boolean;
    message?: string;
    valuesCurrentStep?: StepVM;
  }>({ open: false });
  // stato per errori e warning del bottone VALIDA EVENTO ed ANTEPRIMA
  const [errorWarningToVisualize, setErrorWarningToVisualize] =
    useState<ErrorWarningToVisualizeType>({
      visibilityError: false,
      titleError: '',
      messagesError: [],
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  // stato per stampa anteprima
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{ open: boolean; total?: number }>({
    open: false,
  });
  // tipo evento: 1 = NASCITA, 2 = MORTE, 3 = MATRIMONIO, 4 = UNIONE CIVILE, 5 = CITTADINANZA
  const [tipoEvento, setTipoEvento] = useState<string>('');
  // usecase che effettua la chiamata per i soggetti
  const [UcCallSoggetti, setUcCallSoggetti] = useState<string>('');
  // usecase che effettua la chiamata per le sezioni
  const [UcCallSezioni, setUcCallSezioni] = useState<string>('');
  // id dell'atto del gemello successivo
  const [idActGemelloSuccessivo, setIdActGemelloSuccessivo] = useState<number | undefined>();
  // titolo vecchio, utile se andiamo in rettifica
  const [oldTitle, setOldTitle] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  const params = useParams();

  useEffect(() => {
    if (params.idEvento) {
      setStateAct((oldState) => ({ ...oldState, idEvento: Number.parseInt(params.idEvento, 10) }));
    }
  }, [params.idEvento]);

  const basePathPre = `/stato-civile/${params.valueCat}/${params.idCat}/crea/${params.idEvento}`;
  const basePathPost = `${basePathPre}/${params.valuePostCat}/${params.idPostCat}`;
  const basePathPreStep = `${basePathPost}/${params.valuePreStep}/${params.idPreStep}`;

  const editPath = '/stato-civile/ricerca-atto-da-compilare';

  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  const getDataSaved = useHttpClient(`${__ACT_API_URL__}/bozza/ricerca/${stateAct.idEvento}`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const [dataSignature, getDataSignature] = useHttpFetch<
    HttpResponse<DataWizardAct>,
    SignatureActVM
  >(`${__ACT_API_URL__}/bozza/info-pre-firma/${stateAct.idEvento}`, {
    onBootstrap: false,
    initialData: {},
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: transformerActSignature,
  });

  const [dataSignatureUSC, getDataSignatureUSC] = useHttpFetch<
    HttpResponse<DataWizardAct>,
    SignatureActUscVM
  >(`${__ACT_API_URL__}/bozza/info-pre-firma/${stateAct.idEvento}`, {
    onBootstrap: false,
    initialData: {},
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: transformerActSignatureUsc,
  });

  const [dataView, getDataView] = useHttpFetch<HttpResponse<ActCategory>, ViewStepVM>(
    `${__ACT_API_URL__}/consultazione/evento`,
    {
      onBootstrap: false,
      initialData: {},
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
      transformer: transformerActAnsc,
    }
  );

  const onGetDataView = useEventCallback((idAnsc?: string) => {
    if (!idAnsc) {
      return;
    }

    getDataView({
      data: {
        criteriRicercaEvento: {
          idAnsc,
        },
      },
    });
  });

  const onChangeState = useHttpClient('', {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onDeleteBack = useHttpClient(
    `${__ACT_API_URL__}/wizard/rimuovi/idEvento/${stateAct.idEvento}`,
    {
      options: {
        method: 'DELETE',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onValid = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onForceValid = useHttpClient(`${__ACT_API_URL__}/evento/forza-validazione`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onPreview = useHttpClient(`${__ACT_API_URL__}/evento/anteprima/${stateAct.idEvento}`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onPrint = useHttpClient(
    `${__ACT_API_URL__}/evento/stampa-registro-emergenza/${stateAct.idEvento}`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const deleteData = useHttpClient(`${__ACT_API_URL__}/bozza/rimuovi/${stateAct.idEvento}`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const saveSignatureUSCData = useHttpClient(`${__ACT_API_URL__}/firme/firma-usc`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const sendCheck = useHttpClient(`${__ACT_API_URL__}/controlli-comunicazione`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const saveCertificateConformityData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-attestazione`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [requests, getRequests] = useHttpFetch<HttpResponse<ActWizard>, RequestsType>(
    `${__ACT_API_URL__}/wizard/questionario`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          codStep: params.idPreStep || undefined,
          idEvento: stateAct.idEvento,
        },
      },
      transformer: transformerActWizard,
    }
  );

  const onGetDataSaved = useEventCallback((isSetRecap: boolean) => {
    if (!stateAct.idEvento) {
      return;
    }

    setIsLoadingDraft(true);
    (
      getDataSaved({}) as unknown as ResponsePromise<
        HttpResponse<DataWizardAct>,
        HttpResponse<DataWizardAct>
      >
    ).then((response) => {
      setIsLoadingDraft(false);
      const castedResponseQuestionario: Array<QuestionarioEvento> =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.questionarioEvento ||
        [];

      const castedResponseStatoCivile: StatoCivile =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.statoCivile || {};

      const castedResponseEvento: Evento =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento || {};

      const castedIdGemello: number | undefined =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload
          .idEventoGemelloSuccessivo || undefined;

      const castedAllegatoList: Array<AttachmentsData> =
        (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.allegatoList || [];

      if (!tipoEvento) {
        setTipoEvento(castedResponseEvento.tipoEvento || '');
      }

      if (castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_CONFERMATO) {
        // se lo stato è confermato inizializzare per le firme
        setStateAct((oldState) => {
          if (!oldState.isSignatureAct && !oldState.isCertificateConformityWithoutSubjects) {
            return {
              idEvento: stateAct.idEvento,
              isSignatureAct: true,
            };
          }

          return oldState;
        });

        return;
      }

      if (castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_FIRMA_PER_CONFORMITA) {
        // se lo stato è confermato inizializzare per le firme
        setStateAct((oldState) => {
          if (!oldState.isSignatureUSC) {
            return {
              idEvento: stateAct.idEvento,
              isSignatureUSC: true,
            };
          }

          return oldState;
        });

        return;
      }

      if (castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_FIRMA_DICHIARANTE) {
        // se lo stato è firma dichiarante inizializzare per le firme USC
        setStateAct({
          isCertificateConformity: true,
          allegatoList: castedAllegatoList,
          idAnsc: castedResponseStatoCivile.idAnsc || '',
          idEvento: stateAct.idEvento,
        });

        return;
      }

      if (castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_FIRMA_COMPLETA_USC) {
        // aggiornare id atto gemello successivo
        if (castedIdGemello) {
          setIdActGemelloSuccessivo((oldState) => {
            if (oldState === castedIdGemello) {
              return oldState;
            }

            return castedIdGemello;
          });
        }

        // se raccolta firme complete inizializzare per mostrare tutto bloccato
        setStateAct((oldState) => {
          if (!oldState.isCompleteSignature) {
            return {
              idEvento: stateAct.idEvento,
              isCompleteSignature: true,
            };
          }

          return oldState;
        });

        onGetDataView(castedResponseEvento.idAnsc || '');

        return;
      }

      const lastStepInitial = transformerActWizardDataSaved(
        response as AxiosResponse<HttpResponse<DataWizardAct>>
      );

      setLastStepInitialData(lastStepInitial);

      const recapInitial: Array<RecapVM> = castedResponseQuestionario.map((item, index) => {
        return {
          step: item.codStep || '',
          request: item.descBreveDomanda || '',
          response: item.descBreveRisposta || '',
          codResponse: item.idRisposta,
          disabled: index < 3 || isDetail,
          codStepSuccessivo: item.cdStepSuccessivo || '',
          tipoAtto: castedResponseStatoCivile.tipoAtto || undefined,
          flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || '',
          parte: castedResponseStatoCivile.parteAtto || undefined,
          serie1: castedResponseStatoCivile.serie1Atto || undefined,
          serie2: castedResponseStatoCivile.serie2Atto || undefined,
          nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
          luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
          tipoResidenzaNascituro: castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
          idAnsc: castedResponseStatoCivile.idAnsc || undefined,
          codiceStatoEvento: castedResponseStatoCivile.codiceStatoEvento || undefined,
          numeroAtto: castedResponseStatoCivile.numeroAtto || undefined,
          tipoContenuto: castedResponseEvento.tipoContenuto || undefined,
          role: castedResponseEvento.ruoloOperatore || undefined,
        } as RecapVM;
      });

      if ((recap || []).length === 0 || isSetRecap) {
        // solamente la prima volta quando è vuoto
        setRecap(recapInitial);
      }

      // chiamata per ultimo step
      (
        getRequests({
          data: {
            codStep:
              castedResponseQuestionario[
                castedResponseQuestionario.length - 1
              ].cdStepSuccessivo?.toString() || undefined,
            idEvento: stateAct.idEvento,
          },
        }) as ResponsePromise<HttpResponse<ActWizard>, RequestsType>
      ).then((responseRequests) => {
        const castedResponse = responseRequests as RequestsType;

        if (castedResponse.requests?.length > 0) {
          return;
        }

        setStateAct({
          idEvento: stateAct.idEvento,
          isLastStep: true,
          isLastStepEditAct: stateAct.isLastStepEditAct,
          isLastStepEmergency:
            castedResponseEvento.codiceStatoEvento === STATES_ACT.ID_STATE_BOZZA_EMERGENZA,
        });

        setIdActGemelloSuccessivo(undefined);
      });
    });
  });

  useEffect(() => {
    if (!stateAct.isSignatureAct) {
      return;
    }

    (getDataSignature() as ResponsePromise<HttpResponse<DataWizardAct>, SignatureActVM>).then(
      (res) => {
        const castedRes = res as SignatureActVM;
        const castedAllegatoList: Array<AttachmentsData> = castedRes.allegatoList || [];
        const signatoriesEmpty = (castedRes.signatories || []).length === 0;

        // TODO: vedere se corretto

        // aggiorna allegatoList / isCertificateConformity solo se cambia realmente
        // setStateAct((prev) => {
        //   const allegatiUguali =
        //     Array.isArray(prev.allegatoList) &&
        //     prev.allegatoList.length === castedAllegatoList.length &&
        //     prev.allegatoList.every(
        //       (a, i) => JSON.stringify(a) === JSON.stringify(castedAllegatoList[i])
        //     );

        //   if (castedAllegatoList.length > 0 && (!allegatiUguali || !prev.isCertificateConformity)) {
        //     return {
        //       allegatoList: castedAllegatoList,
        //       isCertificateConformity: true,
        //     };
        //   }

        //   // se non serve aggiornare la lista allegati, mantieni invariato lo stato
        //   return prev;
        // });

        // aggiorna isCertificateConformityWithoutSubjects solo se necessario
        if (signatoriesEmpty) {
          setStateAct((prev) => {
            if (prev.isCertificateConformityWithoutSubjects) {
              return prev;
            }

            return {
              idEvento: stateAct.idEvento,
              idAnsc: stateAct.idAnsc || castedRes.idAnsc,
              allegatoList: castedAllegatoList,
              isCertificateConformityWithoutSubjects: true,
            };
          });
        }
      }
    );
  }, [stateAct.isSignatureAct, getDataSignature]);

  useEffect(() => {
    if (stateAct.isSignatureUSCWithoutDocument || stateAct.isSignatureUSC) {
      getDataSignatureUSC();
    }
  }, [stateAct.isSignatureUSC, stateAct.isSignatureUSCWithoutDocument]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/wizard/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const checkSaveData = useHttpClient(`${__ACT_API_URL__}/atto/controlli`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const [soggettiUseCase, getSoggettiUseCase] = useHttpFetch<
    HttpResponse<Array<SubjectsWizardUseCase>>,
    Array<SubjectsWizardUseCase>
  >(`${__ACT_API_URL__}/soggetti-use-case`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response: AxiosResponse<HttpResponse<Array<SubjectsWizardUseCase>>>) => {
      const { data } = response;
      return (data?.payload || []) as Array<SubjectsWizardUseCase>;
    },
  });

  const [sezioniUseCase, getSezioniUseCase] = useHttpFetch<
    HttpResponse<Array<SectionWizardUseCase>>,
    Array<SectionWizardUseCase>
  >(`${__ACT_API_URL__}/sezioni-use-case`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response: AxiosResponse<HttpResponse<Array<SectionWizardUseCase>>>) => {
      const { data } = response;
      return ((data?.payload || []) as Array<SectionWizardUseCase>)?.map(
        (item) =>
          ({
            ...item,
            nomeSezione: item.nomeSezione
              ? item.nomeSezione[0] + item.nomeSezione.slice(1).toLowerCase()
              : '',
          } as SectionWizardUseCase)
      ) as Array<SectionWizardUseCase>;
    },
  });

  const sezioniUseCaseData: Array<SectionWizardUseCase> | undefined = useMemo(() => {
    return (sezioniUseCase?.data as Array<SectionWizardUseCase>)?.map(
      (item) =>
        ({
          ...item,
          configurazione: item.configurazione?.trim(),
          key: {
            ...(item.key || {}),
            codSezione: item.key?.codSezione?.trim(),
          },
        } as SectionWizardUseCase)
    );
  }, [sezioniUseCase?.data]);

  const soggettiUseCaseData: Array<SubjectsWizardUseCase> | undefined = useMemo(() => {
    return (soggettiUseCase?.data as Array<SubjectsWizardUseCase>)?.map(
      (item: SubjectsWizardUseCase) =>
        ({
          ...item,
          configurazione: item.configurazione?.trim(),
          codSoggetto: item.key?.codSoggetto?.trim(),
        } as SubjectsWizardUseCase)
    );
  }, [soggettiUseCase?.data]);

  useEffect(() => {
    const codLastStep =
      recap[recap.length - 1]?.codStepSuccessivo || recap[recap.length - 1]?.step || '';

    if (!Boolean(codLastStep)) {
      return;
    }

    if (!(stateAct.isLastStep || stateAct.isCompleteSignature)) {
      return;
    }

    if (!soggettiUseCase.isLoading && codLastStep !== UcCallSoggetti) {
      (
        getSoggettiUseCase({
          data: {
            codUseCase: codLastStep,
          },
        }) as unknown as ResponsePromise<Array<SubjectsWizardUseCase>>
      ).then(() => {
        setUcCallSoggetti(codLastStep);
      });
    }

    if (!sezioniUseCase.isLoading && codLastStep !== UcCallSezioni) {
      (
        getSezioniUseCase({
          data: {
            codUseCase: codLastStep,
          },
        }) as unknown as ResponsePromise<Array<SectionWizardUseCase>>
      ).then(() => {
        setUcCallSezioni(codLastStep);
      });
    }
  }, [stateAct, recap, UcCallSoggetti, UcCallSezioni]);

  // step con soggetti, sezioni ed allegati. Per la modifica di un vecchio atto
  const lastStepEdit: boolean = useMemo(() => {
    if (!stateAct.isLastStep && !sezioniUseCaseData) {
      return false;
    }

    // se siamo sull'ultimo step ed è presenta la sezione di ricerca dello stato da modificare cambiare lo stato
    if (
      Boolean(
        sezioniUseCaseData?.find(
          (itemSection) => itemSection.key?.codSezione === CodeSection.ATTO_DA_RETTIFICARE
        )
      )
    ) {
      return true;
    }

    return false;
  }, [sezioniUseCaseData, stateAct.isLastStep]);

  const withReasonDelay: boolean = useMemo(() => {
    return Boolean(recap.find((item) => item.codResponse?.toString() === codResponseDelay));
  }, [recap, codResponseDelay]);

  const createSchema = useEventCallback((requestsData: RequestsType) => {
    const validation: Record<string, unknown> = {};

    requestsData.requests?.forEach((item) => {
      if (item.type == IdType.Checkbox) {
        return;
      }
      validation[item.name] = yup.string().required('Il campo è obbligatorio');
    });

    return validation;
  });

  const validationSchema = useMemo(() => {
    if (stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument) {
      return schemaSignatureUsc;
    }

    if (stateAct.isCertificateConformity || stateAct.isCertificateConformityWithoutSubjects) {
      return schemaCertificateConformity;
    }

    if (stateAct.isLastStep) {
      const inputCreateSoggetti: Array<InputCreate> = (soggettiUseCaseData || []).map(
        (itemSubject) => ({
          configuration: itemSubject?.configurazione || '',
          presenza: itemSubject?.presenza || '',
          keySubject: `subject-${itemSubject?.codSoggetto}`,
          codeSubject: itemSubject?.codSoggetto || '',
          isDichiarante: itemSubject?.flagDichiarante === '1',
          isFirmatario: itemSubject?.flagFirmatario === '1',
        })
      );

      const inputCreateSezioni: Array<InputCreate> = (sezioniUseCaseData || []).map(
        (itemSection) => ({
          configuration: itemSection?.configurazione || '',
          keySubject: `section-${itemSection?.key?.codSezione}`,
          presenza: itemSection?.presenza || '',
        })
      );

      const dateFormationAct: DateTime = (requests?.data as RequestsType)?.dateFormationAct
        ? DateTime.fromISO((requests?.data as RequestsType)?.dateFormationAct || '')
        : DateTime.now();

      return createSchemaSubjectSectionStep(
        inputCreateSoggetti.concat(inputCreateSezioni),
        dateFormationAct,
        withReasonDelay
      ) as yup.ObjectSchema<ObjectShape, Record<string, unknown>>;
    }

    if (!requests?.data) {
      return;
    }
    const validation: yup.ObjectSchema<ObjectShape, Record<string, unknown>> = yup.object(
      createSchema(requests?.data as RequestsType) as ObjectShape
    );

    return validation;
  }, [
    requests?.data,
    stateAct.isLastStep,
    soggettiUseCaseData,
    sezioniUseCaseData,
    stateAct.isSignatureUSC,
    stateAct.isSignatureUSCWithoutDocument,
    withReasonDelay,
  ]);

  const onSave = useEventCallback(
    async (
      valuesCurrentStep: StepVM,
      changeRecap?: boolean,
      withoutCheck?: boolean
    ): Promise<DataWizardAct | undefined> => {
      let recapCopy = recap.slice();

      if (!stateAct.isLastStep) {
        const name = (requests?.data as RequestsType)?.requests[0]?.name;

        if (valuesCurrentStep[name]?.toString()) {
          // l'operatore ha selezionato anche lo step corrente quindi va aggiunto al riepilogo per il salvataggio

          const findElement = (requests?.data as RequestsType).requests[0].options?.find(
            (item) => item.id?.toString() === valuesCurrentStep[name]?.toString()
          );

          recapCopy = recapCopy.concat([
            {
              responseData: findElement?.responseData,
            },
          ]);
        }
      }
      const subjects: Array<ParentVM> = [];

      if (stateAct.isLastStep) {
        const objectKeys = Object.keys(valuesCurrentStep);
        objectKeys.forEach((keyCurr) => {
          if (keyCurr.includes('subject')) {
            subjects.push(valuesCurrentStep[keyCurr] as ParentVM);
          }
        });
      }

      const sections: Array<SectionVM> = [];

      if (stateAct.isLastStep) {
        const objectKeys = Object.keys(valuesCurrentStep);
        objectKeys.forEach((keyCurr) => {
          if (keyCurr.includes('section')) {
            sections.push(valuesCurrentStep[keyCurr] as SectionVM);
          }
        });
      }

      const requestsData = requests?.data as RequestsType;

      const data = transformerActWizardToSave(
        false,
        recapCopy,
        stateAct.idEvento,
        (requests?.data as RequestsType)?.dateFormationAct,
        subjects,
        sections,
        valuesCurrentStep?.textAct || undefined,
        requestsData.idValore?.toString(),
        requestsData.descrizioneValore,
        stateAct.isLastStepEmergency
      );

      let isWithError = false;

      if (!withoutCheck) {
        await (
          checkSaveData({
            data,
          }) as unknown as ResponsePromise<
            HttpResponse<{ errori: string; warning?: string }>,
            HttpResponse<{ errori: string; warning?: string }>
          >
        ).then((response) => {
          const castedResponseErrori = (response as AxiosResponse<HttpResponse<{ errori: string }>>)
            .data.payload.errori;

          const castedResponseWarning = (
            response as AxiosResponse<HttpResponse<{ warning: string }>>
          ).data.payload.warning;

          if (castedResponseErrori || castedResponseWarning) {
            setModalErrorSave({
              open: true,
              message: castedResponseErrori || castedResponseWarning,
              valuesCurrentStep,
            });
            isWithError = true;
          }
        });
      }

      if (isWithError) {
        return;
      }

      let res: DataWizardAct = {};

      await (
        saveData({
          data,
        }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const castedResponseStatoCivile: StatoCivile =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.statoCivile || {};
        const castedResponseEvento: Evento =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento || {};

        const recapNew = recap.map((item) => ({
          ...item,
          flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || undefined,
          nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
          luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
          tipoResidenzaNascituro: castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
        }));

        // aggiornamenti recap
        if (changeRecap) {
          // aggiungere domanda al recap
          const name = (requests?.data as RequestsType)?.requests[0]?.name;

          const findElement = (requests?.data as RequestsType).requests[0].options?.find(
            (item) => item.id?.toString() === valuesCurrentStep[name]?.toString()
          );

          setRecap(
            recapNew.concat([
              {
                step: (requests?.data as RequestsType).step?.toString() || '',
                request: (requests?.data as RequestsType).requests[0].request || '',
                response: findElement?.value,
                parte: findElement?.parte,
                serie1: findElement?.serie1,
                serie2: findElement?.serie2,
                codResponse: findElement?.codResponse,
                codStepSuccessivo: findElement?.id?.toString(),
                tipoAtto: castedResponseStatoCivile.tipoAtto || undefined,
                responseData: findElement?.responseData,
                flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || undefined,
                nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
                luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
                tipoResidenzaNascituro:
                  castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
                tipoContenuto: castedResponseEvento.tipoContenuto || undefined,
              },
            ])
          );
        } else {
          setRecap(recapNew);
        }

        if (stateAct.isLastStep) {
          res = (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload;
        }
      });

      return stateAct.isLastStep ? res : undefined;
    }
  );

  const onYesModalErrorSave = useEventCallback(() => {
    onSave(modalErrorSave.valuesCurrentStep, false, true);
    setModalErrorSave({ open: false });
  });

  const onSubmitSignatureUSC = useEventCallback((values: SignatureActUscVM) => {
    const data = {
      idSoggettoSide: null, // da lasciare a null al momento
      idEventoSide: stateAct.idEvento?.toString() || undefined,
      firmaUscRequest: {
        firmaInput: {
          idEvento:
            values.idAnsc && values.idAnsc.split('-') ? values.idAnsc.split('-')[1] : undefined,
          parametriFirma: {
            idTipoFirma: '1', // fixed
            idModalitaInput: '2', // fixed
            inputFirma1: values.user ? Buffer.from(values.user).toString('base64') : undefined,
            inputFirma2: values.pin ? Buffer.from(values.pin).toString('base64') : undefined,
            inputFirma3: values.otp ? Buffer.from(values.otp).toString('base64') : undefined,
          },
        },
      },
      dataValiditaEventoRequest: values.isAnagrafica
        ? {
            idEvento:
              values.idAnsc && values.idAnsc.split('-') ? values.idAnsc.split('-')[1] : undefined,
            idAnsc: values.idAnsc || undefined,
            dataValidita: values.dateValidity
              ? values.dateValidity.toFormat('yyyy-MM-dd')
              : undefined,
          }
        : undefined,
    };

    (
      saveSignatureUSCData({
        data,
      }) as ResponsePromise<void>
    ).then((resSignatureUSCData) => {
      // reset vecchio titolo
      setOldTitle('');

      // invio PEC
      sendCheck({
        data: {
          idEvento: stateAct.idEvento,
          canaleComunicazione: 'FIRMAEVENT', // fixed
        },
      });

      const castedIdGemello: number | undefined = (
        resSignatureUSCData as unknown as AxiosResponse<
          HttpResponse<{ idEventoGemelloSuccessivo?: number }>
        >
      )?.data?.payload?.idEventoGemelloSuccessivo;
      setIdActGemelloSuccessivo(castedIdGemello);

      // ricaricare dati da ANSC
      setStateAct({
        idEvento: stateAct.idEvento,
        isCompleteSignature: true,
      });

      onGetDataView(values.idAnsc);
    });
  });

  const onSubmitCertificateConformity = useEventCallback((values: SignatureActUscVM) => {
    const data = {
      idSoggettoSide: null, // da lasciare a null al momento
      idEventoSide: stateAct.idEvento,
      firmaAttestazioneRequest: {
        firmaInput: {
          idEvento:
            values.idAnsc && values.idAnsc.split('-') ? values.idAnsc.split('-')[1] : undefined,
          parametriFirma: {
            idTipoFirma: '1', // fixed
            idModalitaInput: '2', // fixed
            inputFirma1: values.user ? Buffer.from(values.user).toString('base64') : undefined,
            inputFirma2: values.pin ? Buffer.from(values.pin).toString('base64') : undefined,
            inputFirma3: values.otp ? Buffer.from(values.otp).toString('base64') : undefined,
          },
        },
      },
    };

    (
      saveCertificateConformityData({
        data,
      }) as ResponsePromise<void>
    ).then(() => {
      // ricaricare dati da ANSC
      setStateAct({
        idEvento: stateAct.idEvento,
        isSignatureUSC: true,
      });

      onGetDataView(values.idAnsc);
    });
  });

  const onSubmit = useEventCallback((values: StepVM) => {
    if (stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument) {
      onSubmitSignatureUSC(values as SignatureActUscVM);
      return;
    }

    if (stateAct.isCertificateConformity || stateAct.isCertificateConformityWithoutSubjects) {
      onSubmitCertificateConformity(values as SignatureActUscVM);
      return;
    }

    // salvataggio
    onSave(values, true);

    const name = (requests?.data as RequestsType)?.requests[0]?.name;
    // è impostata con una sola domanda
    (
      getRequests({
        data: {
          codStep: values[name]?.toString() || undefined,
          idEvento: stateAct.idEvento,
        },
      }) as ResponsePromise<HttpResponse<ActWizard>, RequestsType>
    ).then((responseRequests) => {
      const castedResponse = responseRequests as RequestsType;

      if (castedResponse.requests?.length > 0) {
        return;
      }

      setStateAct((oldState) => {
        if (!oldState.isLastStep) {
          return {
            idEvento: stateAct.idEvento,
            isLastStep: true,
          };
        }

        return oldState;
      });

      setIdActGemelloSuccessivo(undefined);
    });
  });

  const onClickRecap = useEventCallback((step: string, indexCurrent: number) => {
    (onDeleteBack() as ResponsePromise<void>).then(() => {
      // eliminare le domande successive dal recap e ricaricare la domanda dello step richiesto
      (
        getRequests({
          data: {
            codStep: step || undefined,
            idEvento: stateAct.idEvento,
          },
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        // resettare se ultimo step
        setStateAct({
          idEvento: stateAct.idEvento,
          isSignatureAct: false,
          isLastStep: false,
          isSignatureUSC: false,
        });

        const recapCopy = recap.slice();
        const newRecap: Array<RecapVM> = [];

        recapCopy.forEach((item, index) => {
          if (index >= indexCurrent) {
            return;
          }

          newRecap.push(item);
        });

        setRecap(newRecap);
      });
    });
  });

  const goBack = useEventCallback(() => {
    if (isTranscribeOathFromAnsc) {
      const idAnsc = params.id || undefined;
      navigate({
        pathname: generatePath('/stato-civile/ricerca-atti/visualizza/:id', {
          id: idAnsc || '',
        }),
      });
    }

    if (stateAct.isLastStepEditAct) {
      // se ci troviamo in modifica atto, tornare allo step di ricerca "atto da modificare"
      setStateAct({
        isLastStep: true,
        idEvento: Number.parseInt(params.idEvento, 10),
      });

      return;
    }

    if (stateAct.isTranscribeOath) {
      // se ci troviamo in trascrizione giuramento, tornare allo step di ricerca "atto da modificare"
      setStateAct({
        isCompleteSignature: true,
        idEvento: Number.parseInt(params.idEvento, 10),
      });

      return;
    }

    // tornare sullo step precedente
    if (isDetail || stateAct.isCompleteSignature) {
      // l'iter è completo quindi uscire dall'atto
      navigate(editPath);

      return;
    }

    setDeleteAlertModal({
      open: true,
      step: recap[recap.length - 1].step || '',
      index: recap.length - 1,
      message: messageModalGoBack,
    });
  });

  const onSaveSubject = useEventCallback(
    (valuesSubject: ParentVM): ResponsePromise<number | undefined> | undefined => {
      // salvataggio dell'accordion del singolo soggetto (ci troviamo all'ultimo step)
      if (!valuesSubject?.generality) {
        return undefined;
      }

      const arrSubjects: Array<ParentVM> = [valuesSubject];

      const requestsData = requests?.data as RequestsType;

      const data = transformerActWizardToSave(
        true,
        recap,
        stateAct.idEvento,
        (requests?.data as RequestsType)?.dateFormationAct,
        arrSubjects,
        [],
        undefined,
        requestsData.idValore?.toString(),
        requestsData.descrizioneValore,
        stateAct.isLastStepEmergency
      );
      let idSubject = valuesSubject?.generality?.idSubject || undefined;

      return (
        saveData({
          data,
        }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const castedResponseStatoCivile: StatoCivile =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.statoCivile || {};
        const castedResponseEvento: Evento =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.evento || {};

        const recapNew = recap.map((item) => ({
          ...item,
          flagAusilioInterprete: castedResponseEvento.flagAusilioInterprete || undefined,
          nascitaIstat: castedResponseStatoCivile.nascitaIstat || undefined,
          luogoEvento: castedResponseStatoCivile.luogoEvento || undefined,
          tipoResidenzaNascituro: castedResponseStatoCivile.tipoResidenzaNascituro || undefined,
        }));

        setRecap(recapNew);

        const castedResponseSoggettoList: Array<SoggettoList> =
          (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload.soggettoList || [];

        castedResponseSoggettoList.forEach((item) => {
          if (item?.codiceSoggetto === valuesSubject.codeSubject) {
            idSubject = item.key?.idSoggetto || undefined;
          }
        });

        return idSubject;
      }) as ResponsePromise<number | undefined>;
    }
  );

  const onSaveSection = useEventCallback(
    (valuesSection: SectionVM): ResponsePromise<DataWizardAct> | undefined => {
      // salvataggio dell'accordion della singola sezione (ci troviamo all'ultimo step)

      const arrSection: Array<SectionVM> = [valuesSection];

      const requestsData = requests?.data as RequestsType;

      const data = transformerActWizardToSave(
        true,
        recap,
        stateAct.idEvento,
        (requests?.data as RequestsType)?.dateFormationAct,
        [],
        arrSection,
        undefined,
        requestsData.idValore?.toString(),
        requestsData.descrizioneValore,
        stateAct.isLastStepEmergency
      );

      return (
        saveData({
          data,
        }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        return (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload;
      }) as ResponsePromise<DataWizardAct>;
    }
  );

  const onSaveText = useEventCallback(
    (valuesText?: TextActVM): ResponsePromise<DataWizardAct> | undefined => {
      // salvataggio testo dell'atto (ci troviamo all'ultimo step)

      const requestsData = requests?.data as RequestsType;

      const data = transformerActWizardToSave(
        true,
        recap,
        stateAct.idEvento,
        (requests?.data as RequestsType)?.dateFormationAct,
        [],
        [],
        valuesText,
        requestsData.idValore?.toString(),
        requestsData.descrizioneValore,
        stateAct.isLastStepEmergency
      );

      return (
        saveData({
          data,
        }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
      }) as ResponsePromise<DataWizardAct>;
    }
  );

  const idUseCase: string = useMemo(() => {
    const requestsData = requests?.data as RequestsType;

    return requestsData?.idValore?.toString() || '';
  }, [requests?.data]);

  const title = useMemo(() => {
    const requestsData = requests?.data as RequestsType;
    if (idUseCase || requestsData?.descrizioneValore) {
      // last step

      const lastRecap = recap[recap.length - 1];

      return `${lastRecap?.parte || ''}${lastRecap?.serie1 || ''}${lastRecap?.serie2 || ''} ${
        idUseCase ? `[${idUseCase}]` : ''
      } ${requestsData?.descrizioneValore || ''}`.trim();
    }

    if (params.sottoMenuName?.toLowerCase() === 'nuovo') {
      return 'Stesura nuovo evento';
    }
    return 'Compilazione atto';
  }, [params.sottoMenuName, requests?.data, recap, idUseCase]);

  const handleClickRecap = useEventCallback((step: string, index: number) => {
    setDeleteAlertModal({
      open: true,
      step,
      index,
      message: messageModalGoBack,
    });
  });

  const onYesBack = useEventCallback(() => {
    if (recap.length === 3) {
      // tornare sulla navigazione perchè ancora nessun step è stato inserito
      navigate(isEdit ? editPath : basePathPost);
    }

    setUcCallSezioni('');
    setUcCallSoggetti('');
    onClickRecap(deleteAlertModal.step || '', deleteAlertModal.index || 0);
    setDeleteAlertModal({ open: false, step: '', index: 0, message: '' });
  });

  const onClickValid = useEventCallback(() => {
    const listUCAlternative = ['14850000', '2350', '369000', '459000', '53800'];
    const isAlternativeValid = listUCAlternative.includes(idUseCase);

    const url = isAlternativeValid
      ? `${__ACT_API_URL__}/evento/ann-modificativa/valida/${stateAct.idEvento}`
      : stateAct.isLastStepEditAct && stateAct.idEvento && params.idEvento
      ? `${__ACT_API_URL__}/evento/rettifica/valida/${stateAct.idEvento}/${params.idEvento}`
      : `${__ACT_API_URL__}/evento/valida/${stateAct.idEvento}`;

    (
      onValid({
        url,
      }) as unknown as ResponsePromise<
        HttpResponse<ResponseValidateEvent>,
        HttpResponse<ResponseValidateEvent>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');
      setDeleteAlertModal({
        open: false,
      });

      const castedErrors: Array<ErrorsWarnings> =
        (response as AxiosResponse<HttpResponse<ResponseValidateEvent>>).data.payload.errors || [];

      const forcingCode: string =
        (response as AxiosResponse<HttpResponse<ResponseValidateEvent>>).data.payload.forcingCode ||
        '';

      if (castedErrors.length) {
        // se ci sono errori far scegliere se forzare la validazione
        setDeleteAlertModal({
          open: true,
          message: castedErrors.map((item) => item.text)?.join(', '),
          errors: castedErrors,
          forcingCode,
        });
        return;
      }

      // una volta inviato ad ANSC è il momento della raccolta delle firme
      setStateAct({
        isSignatureAct: true,
        idEvento: Number.parseInt(params.idEvento, 10),
      });

      // resettare il recap
      setRecap([]);
    });
  });

  const onEditAct = useHttpClient(`${__ACT_API_URL__}/wizard/modifica-annotazione`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickEditAct = useEventCallback((valuesSection?: SectionVM) => {
    const actSearched = valuesSection?.actForm?.actSearched;
    const idAnsc = valuesSection?.actForm?.idAnsc;

    const idEventoCollegato = valuesSection?.actForm?.idEventoCollegato;

    if (idEventoCollegato) {
      setStateAct({
        isLastStepEditAct: true,
        isLastStep: true,
        idEvento: Number.parseInt(idEventoCollegato.toString(), 10),
      });
      toast.success('Operazione eseguita con successo');

      // mantenere vecchio titolo
      setOldTitle(title);

      return;
    }

    if (!actSearched && !idAnsc) {
      return;
    }

    (
      onEditAct({
        data: {
          modelEvento: actSearched,
          idAnsc,
          idEventoAnnotazione: stateAct.idEvento,
        },
      }) as unknown as ResponsePromise<HttpResponse<DataWizardAct>, HttpResponse<DataWizardAct>>
    ).then((response) => {
      // mantenere vecchio titolo
      setOldTitle(title);

      // ricaricare con idEvento nella key dell'atto da modificare
      const newIdEvento = (response as AxiosResponse<HttpResponse<DataWizardAct>>).data.payload
        .evento?.key?.idEvento;

      if (newIdEvento) {
        setStateAct({ isLastStepEditAct: true, isLastStep: true, idEvento: newIdEvento });
        toast.success('Operazione eseguita con successo');
      } else {
        toast.error('Errore in creazione del nuovo atto');
      }
    });
  });

  const onYesWithErrors = useEventCallback(() => {
    (
      onForceValid({
        data: {
          evento: {
            id: stateAct.idEvento,
          },
          errors: deleteAlertModal.errors || undefined,
          forcingCode: deleteAlertModal.forcingCode || undefined,
        },
      }) as unknown as ResponsePromise<unknown>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      setDeleteAlertModal({
        open: false,
      });

      // una volta inviato ad ANSC è il momento della raccolta delle firme
      setStateAct({ idEvento: stateAct.idEvento, isSignatureAct: true });

      // resettare il recap
      setRecap([]);
    });
  });

  const onClickPreview = useEventCallback(() => {
    setDeleteAlertModal({
      open: true,
      message: messageModalPreview,
    });
  });

  const onYesPreview = useEventCallback(() => {
    setModalPrintSuccess({ open: false });

    (
      onPreview() as unknown as ResponsePromise<
        HttpResponse<ResponsePreview>,
        HttpResponse<ResponsePreview>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      setDeleteAlertModal({
        open: false,
      });

      const responseCasted = response as AxiosResponse<HttpResponse<ResponsePreview>>;

      const castedErrors: Array<ErrorsWarnings> = responseCasted?.data.payload.errors || [];
      const titleError =
        castedErrors?.length > 1 ? `Ci sono ${castedErrors?.length} errori` : `C'è 1 errore`;
      const messagesError =
        (castedErrors?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      const castedWarnings: Array<ErrorsWarnings> = responseCasted?.data.payload.warnings || [];
      const titleWarning =
        castedWarnings?.length > 1 ? `Ci sono ${castedWarnings?.length} warning` : `C'è 1 warning`;
      const messagesWarning =
        (castedWarnings?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      setErrorWarningToVisualize({
        visibilityError: castedErrors?.length > 0,
        titleError,
        messagesError,
        visibilityWarning: castedWarnings?.length > 0,
        messagesWarning,
        titleWarning,
      });

      if (castedErrors?.length > 0) {
        return;
      }

      // se non ci sono errori effettuare stampa
      const pdf = responseCasted.data.payload.eventoOutput?.contenuto;
      if (pdf) {
        setPdfModal({
          data: pdf,
          name: 'Anteprima atto',
          open: true,
        });
      }
    });
  });

  const onPrintAct = useEventCallback(() => {
    setModalPrintSuccess({ open: false });

    (onPrint({ responseType: 'blob' }) as unknown as ResponsePromise<Blob>).then((response) => {
      toast.success('Operazione eseguita con successo');

      if (response?.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Stampa atto',
            open: true,
          });
        });
      }
    });
  });

  const onConfirmRegistration = useEventCallback(() => {
    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${STATES_ACT.ID_STATE_FIRMA_COMPLETA_USC_EMERGENZA}`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate('/stato-civile');
    });
  });

  const onVisibilityError = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityError: false,
      titleError: '',
      messagesError: [],
    });
  });

  const onVisibilityWarning = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  });

  const deleteEvent = useEventCallback(() => {
    setDeleteAlertModal({
      open: true,
      message: messageModalDelete,
    });
  });

  const onYesDelete = useEventCallback(() => {
    (deleteData() as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate('/stato-civile');
    });
  });

  const backCompilation = useEventCallback((values?: SignatureActUscVM) => {
    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${STATES_ACT.ID_STATE_BOZZA}`,
      }) as ResponsePromise<void>
    ).then(() => {
      if (values?.idEventoCorrelato) {
        // ricaricare i dati dell'ultimo step aggiornando l'idEvento
        setStateAct({
          isLastStep: true,
          isLastStepEditAct: true,
          idEvento: Number.parseInt(values?.idEventoCorrelato, 10),
        });

        return;
      }

      // ricaricare i dati dell'ultimo step
      onGetDataSaved(true);

      setStateAct({
        idEvento: stateAct.idEvento,
        isLastStep: true,
      });
    });
  });

  const signatureUSC = useEventCallback(() => {
    // stato già aggiornato in ID_STATE_FIRMA_DICHIARANTE
    onGetDataSaved(false);
  });

  const saveDocuments = useHttpClient(`${__ACT_API_URL__}/allegato-atti/saveAllegatiList`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  const onForwardCertificateConformity = useEventCallback((values: SignatureActUscVM) => {
    const data: Array<AttachmentsData> = (values.allegatoList || []).map((itemAllegato) => {
      // mettere tutti gli allegati a non selezionato
      return {
        ...itemAllegato,
        flagAttestazione: '0',
        flagInclusoAttestazioneConformita: '0',
      };
    });

    (saveDocuments({ data }) as ResponsePromise<unknown, unknown>).then(() => {
      // ricaricare dati da ANSC
      setStateAct({
        idEvento: stateAct.idEvento,
        isSignatureUSC: true,
      });

      onGetDataView(values.idAnsc);
    });
  });

  useEffect(() => {
    if (isTranscribeOathFromAnsc) {
      setStateAct({ idEvento: stateAct.idEvento, isTranscribeOath: true, isLastStep: true });
    }
  }, [isTranscribeOathFromAnsc]);

  const generateOathTranscript = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const transcribeOath = useEventCallback((currentIdEvento?: number) => {
    if (!currentIdEvento) {
      return;
    }

    (
      generateOathTranscript({
        url: `${__ACT_API_URL__}/bozza/genera/trascrizione-giuramento/${currentIdEvento}`,
      }) as unknown as ResponsePromise<HttpResponse<{ idEvento?: number }>>
    ).then((response) => {
      const oathIdEvento = response?.data.payload.idEvento;

      if (oathIdEvento) {
        toast.success('Operazione eseguita con successo');
        navigate({
          pathname: generatePath('/stato-civile/ricerca-atto-da-compilare/modifica/:idEvento', {
            idEvento: oathIdEvento?.toString(),
          }),
        });
        // setStateAct({ idEvento: stateAct.idEvento, isTranscribeOath: true, isLastStep: true, idEvento: Number.parseInt(oathIdEvento.toString(), 10) });
      } else {
        toast.error("Errore nella generazione dell'atto");
      }
    });
  });

  const verifyData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-dichiarante-elettronica/firmadichiarante/pooling-statoatto`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickVerify = useEventCallback(
    (values?: SignatureActVM): ResponsePromise<number> | void => {
      const data = {
        idSoggettoSide: (values?.signatories || []).map((item) => item.idSubject),
        idEventoSide: stateAct.idEvento?.toString() || undefined,
        firmaDichiaranteOnlinePoolingStatoAttoRequest: {
          idEvento:
            values?.idAnsc && values?.idAnsc.split('-') ? values?.idAnsc.split('-')[1] : undefined,
        },
      };

      return (
        verifyData({ data }) as unknown as ResponsePromise<HttpResponse<{ idState?: number }>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        return response?.data.payload.idState;
      }) as ResponsePromise<number>;
    }
  );

  const verifyDocument = useHttpClient(`${__ACT_API_URL__}/gestione-allegato-atti/verifica`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickVerifyDocument = useEventCallback((document?: TypeFile): ResponsePromise<string> => {
    const data = {
      listaAllegatoAtti: [
        {
          key: {
            idAllegato: document?.idAllegato || undefined,
          },
        },
      ],
    };

    return (
      verifyDocument({
        data,
      }) as unknown as ResponsePromise<
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>,
        HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti>; errori?: Array<string> }>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const castedResponse: Array<AllegatoAtti> =
        (response as AxiosResponse<HttpResponse<{ listaAllegatoAtti?: Array<AllegatoAtti> }>>).data
          .payload.listaAllegatoAtti || [];

      return castedResponse[0]?.codStato || '';
    }) as ResponsePromise<string>;
  });

  const getTextAct = useHttpClient(
    `${__ACT_API_URL__}/bozza/genera/testo-atto/${stateAct.idEvento}`,
    {
      options: {
        method: 'GET',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const initialValues: Record<string, string | boolean | ParentVM | DocumentVM | SectionVM> =
    useMemo(() => {
      const res: Record<string, string | boolean | ParentVM | DocumentVM | SectionVM> = {};

      if (stateAct.isLastStep) {
        // se ci troviamo nell'ultimo step
        soggettiUseCaseData?.forEach((itemSubject) => {
          const keySubject = `subject-${itemSubject?.codSoggetto}`;

          res[keySubject] = {
            ...initialValuesParent,
            eventType: tipoEvento,
            generality: {
              ...initialValuesParent?.generality,
              formatBirthDate:
                itemSubject.configurazione === ConfigurationType.SOGMINA ||
                itemSubject.configurazione === ConfigurationType.BASE02A ||
                itemSubject.configurazione === ConfigurationType.BASEANNI
                  ? { id: CodeFormatBirthDate.Completa, value: valueCompleta }
                  : undefined,
              gender:
                itemSubject?.codSoggetto === CodeSubject.PADRE &&
                itemSubject.configurazione === ConfigurationType.BASESD
                  ? ID_GENDERS.MASCHIO
                  : itemSubject?.codSoggetto === CodeSubject.MADRE &&
                    itemSubject.configurazione === ConfigurationType.BASESD
                  ? ID_GENDERS.FEMMINA
                  : '',
              isSignatory:
                itemSubject.configurazione === ConfigurationType.INTCIT4
                  ? lastStepInitialData && (lastStepInitialData[keySubject] as ParentVM)
                    ? (lastStepInitialData[keySubject] as ParentVM).generality?.isSignatory
                    : itemSubject.flagFirmatario === '1'
                  : undefined,
            },
            configuration: itemSubject?.configurazione,
            codeSubject: itemSubject?.codSoggetto,
            flagComparente: itemSubject?.flagComparente || undefined,
            flagDichiarante: itemSubject?.flagDichiarante || undefined,
            tipoSoggetto: itemSubject?.tipoSoggetto || undefined,
            flagFirmatario: itemSubject?.flagFirmatario || undefined,
            presenza: itemSubject?.presenza || undefined,
            ...(lastStepInitialData ? (lastStepInitialData[keySubject] as ParentVM) : {}),
          };
        });

        sezioniUseCaseData?.forEach((itemSection) => {
          const keySection = `section-${itemSection?.key?.codSezione}`;

          const castedCurrSection: SectionVM = (lastStepInitialData[keySection] || {}) as SectionVM;

          res[keySection] = {
            ...initialValuesSections,
            eventType: tipoEvento,
            configuration: itemSection?.configurazione,
            codeSection: itemSection?.key?.codSezione,
            typeSection: itemSection?.tipoSezione,
            typePersistence: itemSection?.tipoPersistenza,
            typePresence: itemSection?.presenza,
            ...(lastStepInitialData ? castedCurrSection : {}),
            // default per configurazione BASEDECA
            numberAppearing:
              itemSection.configurazione === ConfigurationType.BASEDECA
                ? castedCurrSection.numberAppearing || ID_NUMBERS_COMPARENTI.ID_COMPARENTE1
                : castedCurrSection.numberAppearing,
            italian:
              itemSection.configurazione === ConfigurationType.BASEDECA
                ? castedCurrSection.italian || {
                    municipality: MUNICIPALITY_MILANO,
                  }
                : castedCurrSection.italian,
          };
        });

        return {
          ...(res || {}), // initial subjects and sections
          document: (lastStepInitialData?.document as DocumentVM) || undefined, // initial document
          documentEmergencyAct:
            (lastStepInitialData?.documentEmergencyAct as DocumentVM) || undefined, // initial documentEmergencyAct
          textAct: lastStepInitialData?.textAct || undefined, // initial text act
          textAnnotationOld: lastStepInitialData?.textAnnotationOld || undefined,
        };
      }

      if (stateAct.isSignatureAct) {
        // se ci troviamo nelle firme

        return (dataSignature.data || {}) as Record<string, string | boolean>;
      }

      if (stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument) {
        // se ci troviamo nella firma USC
        return (dataSignatureUSC.data || {}) as Record<string, string | boolean>;
      }

      if (stateAct.isCertificateConformity || stateAct.isCertificateConformityWithoutSubjects) {
        // se ci troviamo nella firma per conformità allegati
        if ((stateAct.allegatoList || [])?.length === 0) {
          // se non ci sono documenti passare allo step successivo
          setStateAct({
            idEvento: stateAct.idEvento,
            isSignatureUSCWithoutDocument: true,
          });

          return {};
        }

        const arrDocumentsSelected: Array<number> = [];

        stateAct.allegatoList?.forEach((item) => {
          if (item.flagInclusoAttestazioneConformita === '0') {
            return;
          }

          if (item.key?.idAllegato) {
            arrDocumentsSelected.push(item.key?.idAllegato);
          }
        });
        return {
          allegatoList: stateAct.allegatoList,
          documentsSelected: arrDocumentsSelected,
        } as unknown as Record<string, string | boolean>;
      }

      if (stateAct.isCompleteSignature) {
        // se ci troviamo nella situazione firme complete, tutto in visualizzazione bloccato
        return (dataView.data || {}) as Record<string, string | boolean>;
      }

      // se ci troviamo nella navigazione

      if (requests?.data === undefined) {
        // error
        return res;
      }

      if (((requests?.data as RequestsType)?.requests || []).length === 0) {
        // nessuna risposta, mostrare ultimo step
        return res;
      }

      ((requests?.data as RequestsType)?.requests || []).forEach((item) => {
        res[item.name] = item.type === IdType.Checkbox ? false : '';
      });

      return res;
    }, [
      requests?.data,
      stateAct.isLastStep,
      soggettiUseCaseData,
      sezioniUseCaseData,
      lastStepInitialData,
      stateAct.isSignatureAct,
      dataSignature?.data,
      stateAct.isSignatureUSC,
      stateAct.isSignatureUSCWithoutDocument,
      dataSignatureUSC?.data,
      stateAct.isCompleteSignature,
      dataView?.data,
      stateAct.idEvento,
      stateAct.isCertificateConformity,
      stateAct.isCertificateConformityWithoutSubjects,
      tipoEvento,
      stateAct.allegatoList,
    ]);

  useEffect(() => {
    if (stateAct.isCertificateConformity || stateAct.isCertificateConformityWithoutSubjects) {
      onGetDataSaved(false);
    }
  }, [stateAct.isCertificateConformity, stateAct.isCertificateConformityWithoutSubjects]);

  useEffect(() => {
    onGetDataSaved(true);
  }, [stateAct.idEvento]);

  const updateDataRegistroDiEmergenza = useHttpClient(
    `${__ACT_API_URL__}/bozza/update-dati-registro-emergenza/${stateAct.idEvento}`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onConfirmRegister = useEventCallback(() => {
    if (stateAct.isLastStepEmergency) {
      updateDataRegistroDiEmergenza();
    }

    const newState = stateAct.isLastStepEmergency
      ? STATES_ACT.ID_STATE_BOZZA
      : STATES_ACT.ID_STATE_BOZZA_EMERGENZA;

    (
      onChangeState({
        url: `${__ACT_API_URL__}/bozza/update-stato/${stateAct.idEvento}/${newState}`,
      }) as ResponsePromise<void>
    ).then(() => {
      // ricaricare i dati dell'ultimo step
      onGetDataSaved(false);

      setStateAct({
        idEvento: stateAct.idEvento,
        isLastStep: true,
      });
    });
  });

  const isLoading = sezioniUseCase.isLoading || soggettiUseCase.isLoading || isLoadingDraft;

  const actions = useActionAuthorization({
    anscRegisterEmergency: AnscRegisterEmergency,
  });

  const isWithButtonEmergency = useMemo(() => {
    return (
      Boolean(
        sezioniUseCaseData?.find(
          (item) => item.key?.codSezione === CodeSection.DATI_REGISTRO_EMERGENZA
        )
      ) && actions.anscRegisterEmergency
    );
  }, [sezioniUseCaseData, actions]);

  const commonPath: BreadcrumbItem = {
    name: 'Tipo atto',
    path: basePathPre,
  };

  const crumbs: Array<BreadcrumbItem> = useMemo(() => {
    if (stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument) {
      return [
        ...CommonCivilStatusCrumbs,
        {
          name: `Firma USC per registrare l'atto ${
            (dataSignatureUSC?.data as SignatureActVM)?.idAnsc || ''
          }`,
          path: basePathPreStep,
        },
      ] as Array<BreadcrumbItem>;
    }

    if (stateAct.isCertificateConformity || stateAct.isCertificateConformityWithoutSubjects) {
      [
        ...CommonCivilStatusCrumbs,
        {
          name: `Attestazione di conformità per atto ${stateAct.idAnsc || ''}`,
          path: basePathPreStep,
        },
      ] as Array<BreadcrumbItem>;
    }

    if (stateAct.isSignatureAct) {
      return [
        ...CommonCivilStatusCrumbs,
        {
          name: `Firma atto ${(dataSignature?.data as SignatureActVM)?.idAnsc || ''}`,
          path: basePathPreStep,
        },
      ] as Array<BreadcrumbItem>;
    }

    if (stateAct.isCompleteSignature) {
      return [
        ...CommonCivilStatusCrumbs,
        {
          name: 'Consultazione atto',
          path: basePathPreStep,
        },
      ] as Array<BreadcrumbItem>;
    }

    if (isEdit || isDetail) {
      if (stateAct.isLastStepEditAct) {
        // se stiamo in rettifica e veniamo dalla ricerca atto aggiungere l'atto di ricerca dell'evento da rettificare
        return [
          ...CommonCivilStatusCrumbs,
          {
            name: 'Ricerca atto da completare',
            path: editPath,
          },
          {
            name: `Modifica atto ${oldTitle}`,
            path: `/stato-civile/ricerca-atto-da-compilare/modifica/${params.idEvento}`,
          },
          {
            name: `Rettifica ${title}`,
            path: `/stato-civile/ricerca-atto-da-compilare/modifica/${stateAct.idEvento}`,
          },
        ] as Array<BreadcrumbItem>;
      }

      return [
        ...CommonCivilStatusCrumbs,
        {
          name: 'Ricerca atto da completare',
          path: editPath,
        },
        {
          name: 'Modifica atto',
          path: `/stato-civile/ricerca-atto-da-compilare/modifica/${params.idEvento}`,
        },
      ] as Array<BreadcrumbItem>;
    }

    if (stateAct.isLastStepEditAct) {
      // se stiamo in rettifica e veniamo dalla creazione dell'atto aggiungere l'atto di ricerca dell'evento da rettificare
      return [
        ...CommonCivilStatusCrumbs,
        commonPath,
        {
          name: recap.length >= 3 ? recap[2].request : 'Tipo filiazione',
          path: basePathPost,
        },
        {
          name: oldTitle,
          path: basePathPreStep,
        },
        {
          name: `Rettifica ${title}`,
          path: basePathPreStep,
        },
      ] as Array<BreadcrumbItem>;
    }

    return [
      ...CommonCivilStatusCrumbs,
      commonPath,
      {
        name: recap.length >= 3 ? recap[2].request : 'Tipo filiazione',
        path: basePathPost,
      },
      {
        name: title,
        path: basePathPreStep,
      },
    ] as Array<BreadcrumbItem>;
  }, [stateAct, isEdit, isDetail, params, title, oldTitle]);

  return (
    <Fragment>
      <Breadcrumbs items={crumbs} />
      {isLoading ? undefined : (
        <Formik
          initialValues={initialValues as StepVM}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={onSubmit}
          enableReinitialize
          isInitialValid
          validateOnChange
          validateOnBlur
        >
          <Form>
            <Grid container spacing={2}>
              <TitleStep
                onGetDataSaved={onGetDataSaved}
                onConfirmRegister={onConfirmRegister}
                title={title}
                idEvento={stateAct.idEvento}
                idAnsc={stateAct.idAnsc}
                oldTitle={stateAct.isLastStepEditAct ? oldTitle : ''}
                isLastStep={stateAct.isLastStep}
                isLastStepEditAct={stateAct.isLastStepEditAct}
                isSignatureAct={stateAct.isSignatureAct}
                isSignatureUSC={stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument}
                isLastStepEmergency={stateAct.isLastStepEmergency}
                isCompleteSignature={stateAct.isCompleteSignature}
                isWithButtonEmergency={isWithButtonEmergency}
                isCertificateConformity={
                  stateAct.isCertificateConformity ||
                  stateAct.isCertificateConformityWithoutSubjects
                }
                dateFormationAct={(requests?.data as RequestsType)?.dateFormationAct}
                lastRecap={recap.length > 0 ? recap[recap.length - 1] : undefined}
                disabled={isDetail || stateAct.isCompleteSignature}
                soggettiUseCaseData={soggettiUseCaseData}
                getTextAct={stateAct.isLastStepEditAct ? undefined : getTextAct}
              />
              <StepForm
                goBack={goBack}
                onSave={onSave}
                onClickRecap={handleClickRecap}
                onSaveSubject={onSaveSubject}
                onSaveSection={onSaveSection}
                onSaveText={onSaveText}
                onClickValid={onClickValid}
                onClickEditAct={onClickEditAct}
                onClickPreview={onClickPreview}
                deleteEvent={deleteEvent}
                onYesPreview={onYesPreview}
                onPrintAct={onPrintAct}
                onConfirmRegistration={onConfirmRegistration}
                backCompilation={backCompilation}
                onClickVerify={onClickVerify}
                onClickVerifyDocument={onClickVerifyDocument}
                signatureUSC={signatureUSC}
                transcribeOath={transcribeOath}
                onForwardCertificateConformity={onForwardCertificateConformity}
                dateFormationAct={(requests?.data as RequestsType)?.dateFormationAct}
                isSignatureAct={stateAct.isSignatureAct}
                disabled={isDetail || stateAct.isCompleteSignature}
                isView={stateAct.isCompleteSignature}
                recap={recap}
                setRecap={setRecap}
                requests={requests?.data as RequestsType}
                isLastStep={stateAct.isLastStep}
                isLastStepEditAct={stateAct.isLastStepEditAct}
                isLastStepEdit={lastStepEdit}
                isLastStepEmergency={stateAct.isLastStepEmergency}
                idEvento={stateAct.idEvento}
                soggettiUseCaseData={soggettiUseCaseData}
                sezioniUseCaseData={sezioniUseCaseData}
                isSignatureUSC={stateAct.isSignatureUSC || stateAct.isSignatureUSCWithoutDocument}
                isCertificateConformity={stateAct.isCertificateConformity}
                isCertificateConformityWithoutSubjects={
                  stateAct.isCertificateConformityWithoutSubjects
                }
                isStepFromEmergencyConfirm={isStepFromEmergencyConfirm}
                tipoEvento={tipoEvento}
                getTextAct={stateAct.isLastStepEditAct ? undefined : getTextAct}
                idUseCase={idUseCase}
                idActGemelloSuccessivo={idActGemelloSuccessivo}
                withReasonDelay={withReasonDelay}
                onClickNotification={noop}
                onClickPrintCommunication={noop}
                onParent={noop}
                onOther={noop}
                onCohabitation={noop}
                onRegisterDeath={noop}
                onRegisterMarriageCivilUnion={noop}
                onRegisterCitizenship={noop}
                goBackNotification={noop}
                onCancelNotification={noop}
              />
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Error}
                  title={errorWarningToVisualize.titleError || ''}
                  messages={errorWarningToVisualize.messagesError || []}
                  visibility={errorWarningToVisualize.visibilityError || false}
                  onChangeVisibility={onVisibilityError}
                />
              </Grid>
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Warning}
                  title={errorWarningToVisualize.titleWarning || ''}
                  messages={errorWarningToVisualize.messagesWarning || []}
                  visibility={errorWarningToVisualize.visibilityWarning || false}
                  onChangeVisibility={onVisibilityWarning}
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      )}
      {deleteAlertModal.open ? (
        <DeleteAlertModal
          title="Attenzione!"
          message={deleteAlertModal.message || ''}
          open={deleteAlertModal.open}
          onClose={() => setDeleteAlertModal({ open: false, step: '', index: 0, message: '' })}
          onYes={
            deleteAlertModal.errors
              ? onYesWithErrors
              : deleteAlertModal.message === messageModalGoBack
              ? onYesBack
              : deleteAlertModal.message === messageModalPreview
              ? onYesPreview
              : deleteAlertModal.message === messageModalDelete
              ? onYesDelete
              : noop
          }
          onNo={() => setDeleteAlertModal({ open: false, step: '', index: 0, message: '' })}
          yesError
          labelYes={deleteAlertModal.errors ? 'Prosegui accettando le anomalie' : undefined}
          labelNo={deleteAlertModal.errors ? 'Torna alla compilazione' : undefined}
        />
      ) : undefined}
      {modalErrorSave.open ? (
        <DeleteAlertModal
          title="Attenzione!"
          message={modalErrorSave.message || ''}
          open={modalErrorSave.open}
          onClose={() => setDeleteAlertModal({ open: false, step: '', index: 0, message: '' })}
          onYes={onYesModalErrorSave}
          onNo={() => setModalErrorSave({ open: false })}
          yesError
          labelYes="Prosegui accettando le anomalie"
          labelNo="Torna alla compilazione"
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title="Anteprima atto"
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
            setModalPrintSuccess((oldState) => ({ ...oldState, open: true }));
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => setModalPrintSuccess({ open: false })}
          onClose={() => (stateAct.isLastStepEmergency ? onPrintAct() : onYesPreview())}
        />
      ) : undefined}
    </Fragment>
  );
};

StepPageComponent.displayName = 'Step Page';

const StepPage = StepPageComponent;

export default StepPage;
